import {
  CallLogsApiTelephonyViewsNinjaCallCallLogBusinessGetBusinessCallLogDetailRequest,
  CallLogsApiTelephonyViewsNinjaCallCallLogBusinessGetBusinessCallLogsRequest,
} from "@/api/client";
import { handleError } from "@/api/handleError";
import { LoginResponse } from "@/api/requests/auth";
import {
  getBusinessCallLogDetails,
  getBusinessCallLogs,
} from "@/api/requests/callLogs";
import {
  exportBusinessCallLogsViaEmailFromLink,
  getBusinessCallLogsFromLink,
  getVoiceMailMessagesFromLink,
} from "@/api/requests/misc";
import { getVoiceMailMessages } from "@/api/requests/voiceMailMessage";
import { useAuthStore } from "@/stores";
import { useAddonStore } from "@/stores/add-ons/index.store";
import { useBusinessStore } from "@/stores/businesses.store";
import { useCallStore } from "@/stores/call.store";
import { useNumberStore } from "@/stores/numbers.store";
import {
  EncryptKey,
  EncryptLoginRequest,
  VoiceMailRequestFilterParams,
} from "@/stores/types";
import { useUsersStore } from "@/stores/users.store";
import { PressOneSipCredentials } from "@pressone/dialer/lib/types";
import dayjs from "dayjs";
import { computed } from "vue";
import requestClient from "./api/requestClient";
import { hasBusinessProperty } from "./auth";
import { BASE_API } from "./constants";
import { ReportApiTelephonyViewsNinjaReportTeamExportLeaderboardReportRequest } from "@/api/client";
import { exportLeaderboard } from "@/api/requests/reports";
import { UserActivityEvents } from "./integrations/analytics/backend-events";
import {
  DelegateVerification,
  recommendationData,
  SendSignupPurchaseInvoiceType,
  WebPurchaseData,
  WebPurchaseDataV2,
  webPurchasePayload,
} from "./types/global";
import { CampaignUtmRequest } from "./types/route";
import { formatDateToString, generateFilename } from "./utils";
export interface ResponseData {
  [key: string]: any;
}

export const getActiveBusinessNumberId = () => {
  const numberStore = useNumberStore();
  return numberStore.activeBusinessNumberId;
};
export const getActiveBusinessId = () => {
  void hasBusinessProperty();
  const businessStore = useBusinessStore();
  return businessStore.currentBusinessId;
};
export const getSelectedBusinessNumber = () => {
  // for all business in manager view
  const numberStore = useNumberStore();
  return numberStore.activeBusinessNumberForReports?.id;
};

export const getAddonId = (addonTitle: string) => {
  const addonStore = useAddonStore();
  const addonLists = computed(() => addonStore.getSubscribedAddons);
  const addon = addonLists.value.find((addon) =>
    addon.subscription_plan.title?.includes(addonTitle)
  );
  return addon?.id;
};

export const getReceiverId = () => {
  const userStore = useUsersStore();
  const activeBusinessNumberId = getActiveBusinessNumberId();

  // @ts-expect-error confirm with BE
  return userStore.currentUser?.receivers?.find(
    (x: any) => x.business_number?.id == activeBusinessNumberId
  )?.id;
};

const getUserId = () => {
  const userStore = useUsersStore();
  return userStore?.currentUserBusinessLevel?.id;
};

const getCurrentUserReceiverKey = () => {
  const numberStore = useNumberStore();
  return numberStore.currentUserReceiverKey;
};

export const parseData = async (response: ResponseData) => {
  return response instanceof Promise ? (await response)?.data : response?.data;
  // return response instanceof Promise ? { data: (await response)?.data, status: response.status } : { data: response.data, status: response.status };
};

export const encryptKey = async (payload: { login_id: string }) => {
  return (await requestClient.post(`${BASE_API}/api/v2/auth/encryption-key/`, {
    json: payload,
    level: "public",
  })) as Promise<{
    data: EncryptKey;
  }>;
};
export const verifyEncryptedLoginToken = async (
  payload: EncryptLoginRequest
) => {
  return (await requestClient.post(`${BASE_API}/api/v2/auth/verify-otp`, {
    json: payload,
    level: "public",
  })) as Promise<{ data: LoginResponse }>;
};
export const refreshToken = async (): Promise<ResponseData> => {
  return await requestClient.post(`${BASE_API}/api/auth/token/refresh/`);
};

// export const requestLoginOTP = async (payload: any): Promise<ResponseData> => {
//   return await requestClient.post(`${BASE_API}/api/login/`, payload);
// };

export const requestLoginOTP = async (payload: any): Promise<ResponseData> => {
  return await requestClient.post(`${BASE_API}/api/login/`, {
    json: payload,
    level: "public",
  });
};

export const getUserDetailsByToken = async (): Promise<ResponseData> => {
  return await requestClient.get(`${BASE_API}/api/users/me/`);
};

export const getEncryptionKey = async (): Promise<ResponseData> => {
  return await requestClient.get(`${BASE_API}/public-key/`, {
    level: "public",
  });
};

export const loginWithEmail = async (payload: {
  email: string;
  password: string;
}): Promise<ResponseData> => {
  return await requestClient.post(`${BASE_API}/api/auth/login/email/`, {
    json: payload,
    level: "public",
  });
};

export const sendVerificationEmail = async (payload: {
  email: string;
}): Promise<ResponseData> => {
  return await requestClient.post(`${BASE_API}/api/auth/verification-email/`, {
    json: payload,
    level: "public",
  });
};
// Reset Password API

export const requestResetPaswordEmail = async (payload: {
  email: string;
}): Promise<ResponseData> => {
  return await requestClient.post(
    `${BASE_API}/api/auth/password/reset/request/`,
    { json: payload, level: "public" }
  );
};

export const validateVerifyEmail = async (payload: {
  token: string;
  uid: string;
  is_email_verification?: boolean;
}): Promise<ResponseData> => {
  return await requestClient.post(`${BASE_API}/api/auth/token/validate/`, {
    json: payload,
    level: "public",
  });
};

export const requestPasswordResetConfirmation = async (payload: {
  token: string;
  uid: string;
  new_password: string;
}): Promise<ResponseData> => {
  return await requestClient.post(
    `${BASE_API}/api/auth/password/reset/confirm/`,
    { json: payload, level: "public" }
  );
};
export const changePassword = async (payload: {
  old_password: string;
  new_password: string;
}): Promise<ResponseData> => {
  return await requestClient.post(`${BASE_API}//api/auth/password/change/`, {
    json: payload,
  });
};

// export const getUserDetailsByTokenNewImplementation =
//   async (): Promise<ResponseData> => {
//     return await requestClient.get(`${BASE_API}/api/user/me/`);
//   };

export const getUserDetails = async (): Promise<ResponseData> => {
  return requestClient.get(`${BASE_API}/api/user/me/v2/`);
};

export const updateUserDetailsByTokenNewImplementation = async (payload: {
  home_address: string;
}): Promise<ResponseData> => {
  return await requestClient.patch(`${BASE_API}/api/user/me/`, {
    json: payload,
  });
};

export const updateFeedback = // flag to show a user has updated feedback sheet
  async (): Promise<ResponseData> => {
    return await requestClient.patch(`${BASE_API}/api/user/feedback/`, {});
  };

export const fetchSipInfo = async (payload: {
  public_key: string;
  receiver: string | number;
  mobile?: string | null;
}): Promise<{ data: PressOneSipCredentials }> => {
  return await requestClient.post(
    `${BASE_API}/api/telephony/crm/receiver-line/`,
    { json: payload }
  );
};
export const fetchSipNewInfo = async (
  payload: any
): Promise<{ data: PressOneSipCredentials[] }> => {
  return await requestClient.post(
    `${BASE_API}/api/business/${getActiveBusinessId()}/receiver-line/`,
    { json: payload }
  );
};

// new implementation
export const getCallHistoryNew = async (
  pageSize: string,
  link: string,
  agent_id?: number,
  showAllLogs?: boolean, // check if it manager
  businessId?: number, // when toggling in manager
  source?: string // return only agent log in the recent log partvin agent homepage
) => {
  const callStore = useCallStore();
  const userStore = useUsersStore();
  const numberStore = useNumberStore();

  let requestUrl: string = "";

  const businessNumberId = businessId
    ? businessId
    : userStore.canAccessManagerView
      ? getSelectedBusinessNumber()
      : getActiveBusinessNumberId();

  if (link) {
    const url = new URL(link);
    link = url.pathname + url.search;

    requestUrl = `${BASE_API}${link}`;
  } else
    requestUrl = `${BASE_API}/api/business/${getActiveBusinessId()}/call-log/?page_size=${pageSize}&business_number_id=${businessNumberId}`; // normal link without url
  // in data access mode, its impossible to filter by numbers because the numbers will not be existent anymore
  if (numberStore.subscriptionStatus === "data_access_mode") {
    requestUrl = `${BASE_API}/api/business/${getActiveBusinessId()}/call-log/?page_size=${pageSize}`; // normal link without url
  }

  if (
    (!showAllLogs &&
      !link &&
      !userStore.getAgentRole?.can_view_all_call_logs) ||
    source === "dashboard"
  ) {
    requestUrl += `&user_id=${agent_id ? agent_id : getUserId()}`; // add userid if you arent showing all the logs
  }

  callStore.currentRequestForReload = requestUrl; // stores last request and calls it when reload is called
  callStore.currentRequestQuery =
    link ||
    `user_id=${agent_id ? agent_id : getUserId()}&page_size=${pageSize}`; // stores last query for download and calls it when reload is called
  callStore.fetchFilter = false;

  return await getBusinessCallLogsFromLink({ link: requestUrl });
};

export const filterCallHistoryNew = async (
  filter: any,
  businessId?: string | number
) => {
  const filterString = Object.keys(filter).reduce(
    (acc, key) => {
      const value = filter[key];
      if (value && value !== "") {
        //allow only key value pair with value
        const separator = acc.string ? "&" : ""; // Add '&' only if string is non-empty
        const pair = `${separator}${key}=${value}`;
        return {
          string: acc.string + pair,
        };
      }
      return acc; // Skip if value is falsy
    },
    { string: "" }
  ).string; // Extract the filter string from the accumulator object
  // businessNumberId = businessId ?? getActiveBusinessNumberId();
  const userStore = useUsersStore();
  const numberStore = useNumberStore();

  const businessNumberId = businessId
    ? businessId
    : userStore.canAccessManagerView
      ? getSelectedBusinessNumber()
      : getActiveBusinessNumberId(); // for owner and manager use the list of all number that manager is permitted to access
  let requestUrl: string = `${BASE_API}/api/business/${getActiveBusinessId()}/call-log/?${filterString}&business_number_id=${businessNumberId}`;

  const activeBusinessId = getActiveBusinessId();
  const params: { businessId: number; businessNumberId?: number } = {
    businessId: activeBusinessId ? activeBusinessId : 0,
  };

  if (numberStore.subscriptionStatus === "data_access_mode") {
    requestUrl = `${BASE_API}/api/business/${getActiveBusinessId()}/call-log/?${filterString}`; // normal link without url
  } else {
    params.businessNumberId = Number(businessNumberId);
  }

  const callStore = useCallStore();
  callStore.currentRequestForReload = requestUrl;
  callStore.currentRequestQuery = filterString;
  callStore.fetchFilter = true;
  return await getBusinessCallLogsFromLink({
    link: requestUrl,
    options: {
      params,
    },
  });
};
export const fetchCallLogDetailsById = async (
  call_id: string,
  businessId?: number
) => {
  const payload = {
    businessId: businessId ? businessId : getActiveBusinessId() || "=0",
    id: call_id,
  } as CallLogsApiTelephonyViewsNinjaCallCallLogBusinessGetBusinessCallLogDetailRequest;
  return await getBusinessCallLogDetails(payload);
};

function removePageSizeParam(url: string) {
  // remove param from string
  const urlObj = new URL(url);
  urlObj.searchParams.delete("page_size");
  const updatedUrl = urlObj.toString();
  return updatedUrl.replace(/%2C/g, ",");
}

export const exportCallHistory = async (
  allowCustomDate?: boolean,
  dateFilter?: string
) => {
  let requestUrl: string;

  const callStore = useCallStore();
  callStore.currentRequestForReload;
  const exportString = "export/";

  const index = callStore.currentRequestForReload?.indexOf("?");
  requestUrl =
    callStore.currentRequestForReload?.slice(0, index) +
    exportString +
    callStore.currentRequestForReload?.slice(index);
  if (allowCustomDate) requestUrl += `&start_time_range=${dateFilter}`;
  const exportUrl = removePageSizeParam(requestUrl);

  return await requestClient.download(
    `${exportUrl}`,
    {
      blobType:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
    `pressone_calls_export_${dayjs().format("DD-MM-YYYY")}`
  );
};

export const exportCallHistoryToEmail = (
  recipients: string,
  allowCustomDate?: boolean,
  dateFilter?: string
) => {
  let requestUrl: string;

  const callStore = useCallStore();

  const exportString = "email/export/";

  const index = callStore.currentRequestForReload?.indexOf("?");
  requestUrl =
    callStore.currentRequestForReload?.slice(0, index) +
    exportString +
    callStore.currentRequestForReload?.slice(index);
  if (allowCustomDate) requestUrl += `&start_time_range=${dateFilter}`;
  const exportUrl =
    removePageSizeParam(requestUrl) + `&recipients=${recipients}`;

  return exportBusinessCallLogsViaEmailFromLink({ link: exportUrl });
};

export const downloadBillingHistory = async (
  user_id: string,
  from_date: string,
  to_date: string
) => {
  const fileName = generateFilename(from_date, to_date);
  const business_id = getActiveBusinessId();
  return await requestClient.download(
    `${BASE_API}/api/payments/history/export/?business_id=${business_id}&user_id=${user_id}&from_date=${from_date}&to_date=${to_date}`,
    { blobType: "text/csv" },
    fileName
  );
};

// new implementation

export const fetchTeamMembers = async (): Promise<ResponseData> => {
  return await requestClient.get(
    `${BASE_API}/api/receiver_numbers/?business_number=${getActiveBusinessNumberId()}`
  );
};

export const updateUserDetails = async (payload: any) => {
  return await requestClient.patch(
    `${BASE_API}/api/business/${getActiveBusinessId()}/user/${getUserId()}/profile/`,
    { json: payload }
  );
};

export const updateUserAvatar = async (formData: FormData) => {
  return await requestClient.post(`${BASE_API}/api/users/update_avatar/`, {
    body: formData,
  });
};

export const sendRecommendation = async (
  payload: recommendationData
): Promise<ResponseData> => {
  return parseData(
    await requestClient.post(`${BASE_API}/api/recommendation-form/`, {
      json: payload,
      level: "public",
    })
  );
};

export const getRecommendationByEmail = async (
  email: string
): Promise<ResponseData> => {
  return await requestClient.get(
    `${BASE_API}/api/recommendation-form/by-email/?email=${email}`,
    { level: "public" }
  );
};

export const getRecommendation = async (): Promise<ResponseData> => {
  return await requestClient.get(`${BASE_API}/api/recommendation-form/`);
};

export const updateRecommendation = async (
  presaleId: string,
  payload: any,
  hashId?: string
): Promise<ResponseData> => {
  const authStore = useAuthStore();
  const headers =
    hashId && !authStore.isAuthenticated // pass headers only if not authenticated
      ? { "Pressone-X-Global-ID": hashId }
      : undefined;
  return await requestClient.patch(
    `${BASE_API}/api/recommendation-form/${presaleId}/`,
    {
      json: payload,
      level: !authStore.isAuthenticated ? "public" : "private",
      ...(headers && { extraHeaders: headers }),
    }
  );
};
export const campaignPurchase = async (payload?: {
  plan_id: string;
}): Promise<ResponseData> => {
  return parseData(
    await requestClient.post(
      `${BASE_API}/api/payments/number-purchase/campaign/`,
      { json: payload || {} }
    )
  );
};

export const getWebPaymentPurchaseLinkSignUp = async (
  payload: WebPurchaseData
): Promise<ResponseData> => {
  return parseData(
    await requestClient.post(`${BASE_API}/api/payments/number-purchase/web/`, {
      json: payload,
      level: "public",
    })
  );
};
export const getWebPaymentPurchaseLinkSignUpVersionTwo = async (
  payload: WebPurchaseDataV2
): Promise<ResponseData> => {
  return parseData(
    await requestClient.post(
      `${BASE_API}/api/payments/number-purchase/web/v2/`,
      { json: payload, level: "public" }
    )
  );
};

export const getWebPaymentPurchaseLinkSignUpForAdditionalPurchase = async (
  payload: webPurchasePayload
): Promise<ResponseData> => {
  return parseData(
    await requestClient.post(`${BASE_API}/api/payments/number-purchase/`, {
      json: payload,
    })
  );
};
export const registerUser = async (payload: any) => {
  return await requestClient.post(`${BASE_API}/api/user/register/`, {
    json: payload,
    level: "public",
  });
};
export const registerUserPassword = async (payload: any) => {
  return await requestClient.post(`${BASE_API}/api/auth/register/`, {
    json: payload,
    level: "public",
  });
};

export const getPlans = async (queryString: string = "") => {
  return parseData(
    await requestClient.get(
      `${BASE_API}/api/payments/subscription-plan/?page_size=100` + queryString,
      { level: "public" }
    )
  );
};

export const getActivityReport = async (
  export_csv: boolean,
  from?: string,
  to?: string,
  userId?: number,
  numberId?: number
): Promise<any> => {
  let requestUrl = `${BASE_API}/api/business/report/team/?start_date=${from}&business_id=${getActiveBusinessId()}&end_date=${to}&period=custom&number_id=${numberId}&user_id=${userId}${
    export_csv ? "/export-csv" : ""
  }`;
  if (from && to) {
    if (export_csv) {
      requestUrl = `${requestUrl}?from_date=${from}&to_date=${to}`;
    }
  }

  return export_csv
    ? await requestClient.download(
        requestUrl,
        { blobType: "text/csv" },
        "activity_report.csv"
      )
    : await requestClient.get(requestUrl);
};

export const summaryReportCsv = async (
  custom_start_date: string,
  custom_end_date: string,
  businessNumberId: number
) => {
  const fileTitle = `Pressone summary report(${formatDateToString(
    custom_start_date
  )} - ${formatDateToString(custom_end_date)})`;
  return await requestClient.download(
    `${BASE_API}/api/business/report/team/export-csv/?number_id=${businessNumberId}&business_id=${getActiveBusinessId()}&period=custom&start_date=${custom_start_date}&end_date=${custom_end_date}`,
    { blobType: "text/csv" },
    `${fileTitle}`
  );
};

export const fetchCallSummary = async (period: string) => {
  return await requestClient.get(
    `${BASE_API}/api/business/report/call-log-summary/?business_id=${getActiveBusinessId()}&period=${period}`
  );
};
export const fetchIndividualCallLength = async () => {
  const userStore = useUsersStore();
  const numberStore = useNumberStore();

  if (!getActiveBusinessId()) {
    return;
  }
  let payload: CallLogsApiTelephonyViewsNinjaCallCallLogBusinessGetBusinessCallLogsRequest =
    {
      businessId: getActiveBusinessId() ?? 0,
    };
  if (numberStore.subscriptionStatus !== "data_access_mode") {
    payload = { ...payload, userId: userStore.currentUserBusinessLevel?.id };
  }

  return await getBusinessCallLogs(payload);
};
export const hasMadeTestCall = async () => {
  return await requestClient.get(
    `${BASE_API}/api/business/${getActiveBusinessId()}/number/${getActiveBusinessNumberId()}/call_log/?page_size=25`
  );
};
export const teamMembersReportData = async (
  custom_start_date: string,
  custom_end_date: string
) => {
  return await requestClient.get(
    `${BASE_API}/api/numbers/${getReceiverId()}/team-members-report/?order_by=satisfaction_rate&custom_start_date=${custom_start_date}&custom_end_date=${custom_end_date}&period=custom`
  );
};

export const leaderboardReportData = async (
  from?: string,
  to?: string,
  business_number_id?: string
) => {
  return requestClient.get(
    `${BASE_API}/api/business/report/leaderboard/?number_id=${business_number_id}&business_id=${getActiveBusinessId()}&order_by=total_call&start_date=${from}&end_date=${to}&period=custom`
  );
};

export const downloadLeaderbordReportData = async (
  payload: ReportApiTelephonyViewsNinjaReportTeamExportLeaderboardReportRequest
) => {
  const fileTitle = `Leaderboard Report (${formatDateToString(
    payload.startDate!
  )} - ${formatDateToString(payload.endDate!)})`;

  // We still leverage the existing downloadFileRequest to save filename and handle blob consistently.
  const params = new URLSearchParams();
  params.set("number_id", String(payload.numberId));
  params.set("business_id", String(payload.businessId));
  params.set("period", payload.period);
  params.set("order_by", payload.orderBy);
  if (payload.startDate) params.set("start_date", payload.startDate);
  if (payload.endDate) params.set("end_date", payload.endDate);

  return requestClient.download(
    `${BASE_API}/api/business/report/leaderboard/export-csv/?${params.toString()}`,
    { blobType: "text/csv" },
    `${fileTitle}`
  );
};

export const requestVerificationOTP = async (token: string) => {
  return parseData(
    await requestClient.get(
      `${BASE_API}/api/business/verification/otp/?token=${token}`
    )
  );
};

export const confirmVerificationOTP = async (payload: any) => {
  return parseData(
    await requestClient.post(`${BASE_API}/api/users/confirm-verifation-otp/`, {
      json: payload,
    })
  );
};

export const verifyAccount = async (payload: any) => {
  return parseData(
    await requestClient.post(`${BASE_API}/api/business/verification/`, {
      json: payload,
    })
  );
};

export const delegateVerification = async (payload: DelegateVerification) => {
  return parseData(
    await requestClient.post(
      `${BASE_API}/api/business/verification/delegate/`,
      { json: payload }
    )
  );
};

export const updateUserProfile = async (id: number, payload: any) => {
  return parseData(
    await requestClient.patch(`${BASE_API}/api/users/${id}/`, {
      json: payload,
    })
  );
};

export const getBusinessDetails = async () => {
  return parseData(
    await requestClient.get(
      `${BASE_API}/api/business/${getActiveBusinessId()}/`
    )
  );
};

export const updateBusinessDetails = async (payload: any) => {
  return parseData(
    await requestClient.patch(
      `${BASE_API}/api/business/${getActiveBusinessId()}/`,
      { json: payload }
    )
  );
};
export const getBalance = async () => {
  return parseData(
    await requestClient.get(
      `${BASE_API}/api/business/${getActiveBusinessId()}/balance/`
    )
  );
};

export const submitGovernmentId = async (payload: any) => {
  return parseData(
    await requestClient.post(
      `${BASE_API}/api/users/upload_verification_document/`,
      { body: payload }
    )
  );
};

export const fetchVoicemail = async (
  params: VoiceMailRequestFilterParams,
  businessId?: number | null,
  link?: string
) => {
  const userStore = useUsersStore();

  // allow toggle business id in manager since no active business
  const businessNumberId = businessId
    ? businessId
    : userStore.canAccessManagerView
      ? getSelectedBusinessNumber()
      : getActiveBusinessNumberId(); // for owner and manager use the list of all number that manager is permitted to access

  try {
    if (!link) {
      if (!businessNumberId || !businessId) {
        throw new Error("Business number id not found to get voiceMail");
      }

      const response = await getVoiceMailMessages({
        numberId: businessNumberId,
        businessId,
        pageSize: 100,
        ...params,
      });

      return response;
    } else {
      const response = await getVoiceMailMessagesFromLink({ link });
      return response;
    }
  } catch (error) {
    handleError({ error });
  }
};
export const updateVoicemailStatus = async (voice_message_id: any) => {
  const url = `${BASE_API}/api/business/${getActiveBusinessId()}/voicemail-message/${voice_message_id}/played/`;

  return parseData(await requestClient.patch(url));
};

export const registerInvitedAgent = async (payload: FormData) => {
  return await requestClient.post(`${BASE_API}/api/agent/invite-signup/`, {
    body: payload,
  });
};

export const getMobileFromInvite = async (payload: any) => {
  return await requestClient.get(
    `${BASE_API}/api/agent/manager/get_invited_user_details?code=${payload.code}=&nonce=${payload.nonce}`
  );
};

export const getAllBusiness = async () => {
  return parseData(await requestClient.get(`${BASE_API}/api/business/`));
};
export const getUserPermissions = async () => {
  return parseData(
    await requestClient.get(
      `${BASE_API}/api/number/${getActiveBusinessNumberId()}/agent/${getCurrentUserReceiverKey()}/`
    )
  );
};
export const logoutAndClearCookies = async (token: string) => {
  return parseData(
    await requestClient.post(`${BASE_API}/api/auth/logout/`, {
      extraHeaders: {
        Authorization: `Bearer ${token}`,
      },
      level: "public",
    })
  );
};

export const userActivityEvents = async (payload: UserActivityEvents) => {
  return await requestClient.post(`${BASE_API}/api/event/`, {
    json: payload,
    level: "public",
  });
};
export const loginWithMagicLink = async (payload: string) => {
  return await requestClient.get(
    `${BASE_API}/api/auth/login/magic-link/?token=${payload}`,
    { level: "public" }
  );
};

export const resendMagicLink = async (payload: { email: string }) => {
  return await requestClient.post(
    `${BASE_API}/api/auth/send-magic-login-link/`,
    { json: payload, level: "public" }
  );
};

export const sendSignupPurchaseInvoice = async (
  payload: SendSignupPurchaseInvoiceType
): Promise<ResponseData> => {
  return parseData(
    await requestClient.post(
      `${BASE_API}/api/payments/number-purchase/invoice/`,
      { json: payload, level: "public" }
    )
  );
};
export const fetchCurrentSignupStage = async (hash_id: string) => {
  return parseData(
    await requestClient.get(`${BASE_API}/api/complete-signup/${hash_id}/`, {
      level: "public",
    })
  );
};

export const sendCampaignUTMData = async (payload: CampaignUtmRequest) => {
  return await requestClient.post(`${BASE_API}/api/leads/`, {
    json: payload,
  });
};
